import { identify, init, track, Identify, getDeviceId, getSessionId, getUserId } from "@amplitude/analytics-browser";
import { identifyEnvironment } from "@/utils/IdentifyGlobalEnv";
import { setObservabilityUser } from "@/utils/observability";
import * as Inspector from "avo-inspector";
import { AvoInspectorEnvValueType, AvoInspector } from "avo-inspector";
import { useTemplateStore } from "@/composables/useTemplateStore";
import { useSessionStore } from "@/composables/useSessionStore";

// Global type declaration for document.userProperties
declare global {
  interface Document {
    userProperties: {
      idInTable: string;
      usuarioID: string;
      email: string;
    };
  }
}

// Amplitude API key dictionary based on environment (placeholders to be filled)
const amplitudeApiKeyDictionary = {
  // Local: disabled by default (use .env VUE_APP_AMPLITUDE_API_KEY to enable)
  local: undefined as string | undefined,
  staging: "0861915aeacdb64e82cd5c7f663c1ae2",
  production: "6ad5d4c4d3bacb7d2bbf73f4fda309fe",
};

const avoApiKey = "Yby7g9jmtRfifQMCoUJl";

// Amplitude HTTP endpoint this app sends to (serverZone "EU", no custom serverUrl => default EU host).
// The probe must target the same host so it measures the reachability of OUR events.
const AMPLITUDE_EU_ENDPOINT = "https://api.eu.amplitude.com/2/httpapi";
const PROBE_TIMEOUT_MS = 1000;

// Resolves to true when Amplitude events are blocked on the client (ad-blocker / privacy extension / network),
// false when reachable, and undefined until the probe runs (i.e. Amplitude never initialized).
let blockedCheckPromise: Promise<boolean | undefined> = Promise.resolve(undefined);

async function checkAmplitudeBlocked(): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    await fetch(AMPLITUDE_EU_ENDPOINT, {
      method: "POST",
      mode: "no-cors",
      signal: controller.signal,
    });
    return false;
  } catch {
    return true;
  } finally {
    clearTimeout(timeoutId);
  }
}

function getEnvironmentAmplitudeApiKey(): string | undefined {
  const env = identifyEnvironment();
  if (env === "local") {
    return process.env.VUE_APP_AMPLITUDE_API_KEY || amplitudeApiKeyDictionary.local;
  }
  if (env === "staging") {
    return amplitudeApiKeyDictionary.staging;
  }
  return amplitudeApiKeyDictionary.production;
}

function getAvoInspectorEnv(): AvoInspectorEnvValueType {
  const env = identifyEnvironment();
  if (env === "local") {
    return Inspector.AvoInspectorEnv.Dev;
  }
  if (env === "staging") {
    return Inspector.AvoInspectorEnv.Staging;
  }
  return Inspector.AvoInspectorEnv.Prod;
}

interface RequiredEventProperties {
  page_url: string; // window.location.href
  dp_source: string; // feegow
  event_source: string; // web, mobile, etc
}

interface OptionalEventProperties {
  session_id: string; // current session_id
  device_type: string | undefined; // mobile, desktop, tablet
  template_id: string | undefined; // template_id if any selected
  page_category: string | undefined; // page_category
  element: string | undefined; // element_id if any interaction was targeted
}

type EventProperties = RequiredEventProperties & OptionalEventProperties;

// Type for custom properties passed to trackEvent
type CustomEventProperties = Partial<{
  session_id: string;
  device_type: string;
  template_id: string;
  templateId: string; // Alternative naming
  page_category: string;
  page_section: string;
  page_type: string;
  modal_name: string;
  element: string;
  element_id: string; // Alternative naming
}> &
  Record<string, any>; // Allow additional properties

/**
 * Enhanced UserTracking class with automatic property enrichment
 *
 * Features:
 * - Prevents duplicate user identification calls within the same session
 * - Prevents duplicate events with the same payload and timestamp (within 5-second window)
 * - Automatically enriches event properties with page context
 * - Provides debugging methods for testing
 * - Configurable event deduplication with cache management
 */
class UserTracking {
  private static inspector: AvoInspector | null = null;
  private static userIdentified = false;

  // Event deduplication tracking
  private static recentEvents: Map<string, { payload: any; timestamp: number }> = new Map();
  private static readonly EVENT_DEDUP_WINDOW_MS = 2000; // 2 seconds window for deduplication
  private static deduplicationEnabled = true; // Can be disabled for testing

  /**
   * Check if an event is a duplicate of a recently tracked event
   * @param eventName - The name of the event
   * @param payload - The event payload
   * @returns true if this is a duplicate event
   */
  private static isDuplicateEvent(eventName: string, payload: any): boolean {
    if (!this.deduplicationEnabled) {
      return false; // Skip deduplication if disabled
    }

    const now = Date.now();
    const eventKey = `${eventName}_${JSON.stringify(payload)}`;
    const recentEvent = this.recentEvents.get(eventKey);

    if (recentEvent && now - recentEvent.timestamp < this.EVENT_DEDUP_WINDOW_MS) {
      // console.log(`[UserTracking] Duplicate event detected and skipped: ${eventName}`);
      return true;
    }

    // Store this event for future deduplication
    this.recentEvents.set(eventKey, { payload, timestamp: now });
    return false;
  }

  /**
   * Clean up old events from the deduplication cache
   */
  private static cleanupOldEvents(): void {
    const now = Date.now();
    for (const [key, event] of this.recentEvents.entries()) {
      if (now - event.timestamp > this.EVENT_DEDUP_WINDOW_MS) {
        this.recentEvents.delete(key);
      }
    }
  }

  private static getDeviceType(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/mobile|android|iphone|ipad|phone/.test(userAgent)) {
      return "mobile";
    } else if (/tablet|ipad/.test(userAgent)) {
      return "tablet";
    }
    return "desktop";
  }

  private static getPageCategory(): string | undefined {
    return document.title || window.location.pathname.split("/")[1] || undefined;
  }

  /**
   * Helper function to convert empty strings to undefined
   * @param value - The value to check
   * @returns undefined if the value is an empty string, otherwise the original value
   */
  private static normalizeValue(value: string | undefined): string | undefined {
    return value === "" ? undefined : value;
  }

  /**
   * Get source user ID from document.userProperties.usuarioID
   * @returns The usuarioID if available, undefined otherwise
   */
  private static getSourceUserId(): string | undefined {
    return document?.userProperties?.usuarioID || undefined;
  }

  /**
   * Determine if user is internal based on email domain
   * @returns true if user is internal based on email domain, false otherwise
   */
  private static isInternalUser(): boolean {
    const email = document?.userProperties?.email;
    if (!email || typeof email !== "string") {
      return false;
    }

    const emailLower = email.toLowerCase();
    const internalDomains = ["feegow", "docplanner.com", "doctoralia", "doctortakimi", "damaris.com"];

    return internalDomains.some((domain) => emailLower.includes(domain));
  }

  private static enrichEventProperties(customProps: Record<string, any> = {}): EventProperties {
    // Get current template ID from global store if not provided
    const { templateId } = useTemplateStore();

    // Get current session information from global store
    const { getSessionId } = useSessionStore();

    // Normalize all custom properties to handle empty strings
    const normalizedCustomProps: Record<string, any> = {};
    for (const [key, value] of Object.entries(customProps)) {
      if (typeof value === "string") {
        normalizedCustomProps[key] = this.normalizeValue(value);
      } else {
        normalizedCustomProps[key] = value;
      }
    }

    return {
      // Required properties - automatically detected
      page_url: window.location.href,
      dp_source: "feegow",
      event_source: "web",

      // Optional properties - from custom props or auto-detected (normalized)
      session_id: this.normalizeValue(customProps.session_id || getSessionId() || undefined),
      device_type: this.normalizeValue(customProps.device_type || this.getDeviceType()),
      template_id: this.normalizeValue(
        customProps.template_id || customProps.templateId || templateId.value || undefined
      ),
      page_category: this.normalizeValue(customProps.page_category || this.getPageCategory()),
      element: this.normalizeValue(customProps.element || customProps.element_id || undefined),

      // Merge any additional custom properties (normalized)
      ...normalizedCustomProps,
    };
  }

  /**
   * Track an event with automatic property enrichment
   * @param eventName - The name of the event to track
   * @param customProps - Optional custom properties to include with the event
   */
  static trackEvent(eventName: string, customProps: CustomEventProperties = {}) {
    const enrichedProps = this.enrichEventProperties(customProps);

    // Check for duplicate events
    if (this.isDuplicateEvent(eventName, enrichedProps)) {
      return; // Skip duplicate event
    }

    // Clean up old events periodically
    this.cleanupOldEvents();

    track(eventName, enrichedProps);
  }

  /**
   * Track an event with element targeting
   * @param eventName - The name of the event to track
   * @param elementId - The ID of the element that was interacted with
   * @param customProps - Optional custom properties to include with the event
   */
  static trackElementEvent(eventName: string, elementId: string, customProps: CustomEventProperties = {}) {
    this.trackEvent(eventName, {
      ...customProps,
      element: elementId,
    });
  }

  /**
   * Track an event with template context
   * @param eventName - The name of the event to track
   * @param templateId - The ID of the template being used
   * @param customProps - Optional custom properties to include with the event
   */
  static trackTemplateEvent(eventName: string, templateId: string, customProps: CustomEventProperties = {}) {
    this.trackEvent(eventName, {
      ...customProps,
      template_id: templateId,
    });
  }

  /**
   * Identify user for Amplitude tracking
   * @param options - User identification options
   */
  static identifyUser({
    pms_name = "feegow",
    country_code = "br",
    pms_doctor_id,
    user_type = "professional",
    is_internal_user,
  }: {
    pms_name?: string;
    country_code?: string;
    pms_doctor_id: string;
    user_type?: string;
    is_internal_user?: boolean;
  }) {
    // Prevent duplicate user identification in the same session
    if (this.userIdentified) {
      // console.log("[UserTracking] User already identified in this session, skipping identification");
      return;
    }

    // Auto-detect internal user status if not explicitly provided
    const isInternalUserDetected = is_internal_user !== undefined ? is_internal_user : this.isInternalUser();

    const AMPLITUDE_API_KEY = getEnvironmentAmplitudeApiKey();

    if (AMPLITUDE_API_KEY) {
      // Get source_user_id from document.userProperties.usuarioID
      const sourceUserId = this.getSourceUserId();
      const normalizedSourceUserId = this.normalizeValue(sourceUserId);
      const amplitudeUserId = `${pms_name}-${country_code}-${normalizedSourceUserId}`;

      init(AMPLITUDE_API_KEY, amplitudeUserId, {
        serverZone: "EU",
        autocapture: {
          pageViews: true,
          formInteractions: false,
          fileDownloads: false,
          elementInteractions: false,
        },
      });

      // Kick off the reachability probe once Amplitude is initialized. The verdict is sent to the
      // backend at session creation so server-side events can flag doctors whose client-side
      // analytics never reach Amplitude (FPRT-1165).
      blockedCheckPromise = checkAmplitudeBlocked();

      // Normalize all values to handle empty strings
      const normalizedCountryCode = this.normalizeValue(country_code);
      const normalizedUserType = this.normalizeValue(user_type);
      const normalizedPmsName = this.normalizeValue(pms_name);
      const normalizedPmsUserId = this.normalizeValue(pms_doctor_id);

      const amplitudeIdentify = new Identify();

      // Only set properties that are not undefined
      if (normalizedCountryCode !== undefined) {
        amplitudeIdentify.set("country_code", normalizedCountryCode);
      }
      if (normalizedUserType !== undefined) {
        amplitudeIdentify.set("user_type", normalizedUserType);
      }
      if (normalizedSourceUserId !== undefined) {
        amplitudeIdentify.set("source_user_id", normalizedSourceUserId);
      }
      if (isInternalUserDetected !== undefined) {
        amplitudeIdentify.set("is_internal_user", isInternalUserDetected);
      }
      if (normalizedPmsName !== undefined) {
        amplitudeIdentify.set("pms_name", normalizedPmsName);
      }
      if (normalizedPmsUserId !== undefined) {
        amplitudeIdentify.set("pms_doctor_id", normalizedPmsUserId);
      }

      // Send to Amplitude
      identify(amplitudeIdentify);

      // Attach the same user identity to Datadog RUM (no-op if RUM not initialized).
      // Per spec: only `id` and optional `role` — no PMS/country metadata (those
      // belong on Amplitude, RUM keeps a minimal opaque surface).
      setObservabilityUser({ id: amplitudeUserId });

      // Mark user as identified for this session
      this.userIdentified = true;
      if (
        getAvoInspectorEnv() === Inspector.AvoInspectorEnv.Dev ||
        getAvoInspectorEnv() === Inspector.AvoInspectorEnv.Staging
      ) {
        console.log("[UserTracking] Amplitude initialized with user_id: " + amplitudeUserId);
        console.log("[UserTracking] User identified with properties:", {
          country_code: normalizedCountryCode,
          user_type: normalizedUserType,
          source_user_id: normalizedSourceUserId,
          is_internal_user: isInternalUserDetected,
          pms_name: normalizedPmsName,
          pms_doctor_id: normalizedPmsUserId,
        });
      }
    }
  }

  /**
   * Reset user identification flag (useful for testing or debugging)
   */
  static resetUserIdentification() {
    this.userIdentified = false;
    console.log("[UserTracking] User identification flag reset");
  }

  /**
   * Check if user has been identified in this session
   */
  static isUserIdentified(): boolean {
    return this.userIdentified;
  }

  /**
   * Get the Amplitude device ID (only available after init/identifyUser)
   */
  static getDeviceId(): string | undefined {
    return getDeviceId();
  }

  /**
   * Get the Amplitude session ID (only available after init/identifyUser)
   */
  static getSessionId(): number | undefined {
    return getSessionId();
  }

  /**
   * Get the Amplitude user ID (only available after init/identifyUser)
   */
  static getUserId(): string | undefined {
    return getUserId();
  }

  /**
   * Resolve the Amplitude reachability probe started at identify time.
   * @returns true if Amplitude events are blocked on the client, false if reachable,
   * or undefined if the probe never ran (Amplitude disabled / not initialized).
   */
  static getIsAnalyticsBlocked(): Promise<boolean | undefined> {
    return blockedCheckPromise;
  }

  /**
   * Clear the event deduplication cache (useful for testing or debugging)
   */
  static clearEventCache(): void {
    this.recentEvents.clear();
    console.log("[UserTracking] Event deduplication cache cleared");
  }

  /**
   * Get the current size of the event deduplication cache
   */
  static getEventCacheSize(): number {
    return this.recentEvents.size;
  }

  /**
   * Enable or disable event deduplication
   * @param enabled - Whether to enable deduplication
   */
  static setDeduplicationEnabled(enabled: boolean): void {
    this.deduplicationEnabled = enabled;
    console.log(`[UserTracking] Event deduplication ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Check if event deduplication is currently enabled
   */
  static isDeduplicationEnabled(): boolean {
    return this.deduplicationEnabled;
  }

  /**
   * Get debug information about the deduplication system
   */
  static getDeduplicationDebugInfo(): {
    enabled: boolean;
    cacheSize: number;
    windowMs: number;
    recentEvents: Array<{ key: string; timestamp: number }>;
  } {
    const recentEventsArray = Array.from(this.recentEvents.entries()).map(([key, event]) => ({
      key,
      timestamp: event.timestamp,
    }));

    return {
      enabled: this.deduplicationEnabled,
      cacheSize: this.recentEvents.size,
      windowMs: this.EVENT_DEDUP_WINDOW_MS,
      recentEvents: recentEventsArray,
    };
  }

  /**
   * Test method to verify deduplication functionality
   * Call this from browser console to test: UserTracking.testDeduplication()
   */
  static testDeduplication(): void {
    console.log("[UserTracking] Testing deduplication functionality...");

    // Clear cache first
    this.clearEventCache();

    // Test 1: First event should be tracked
    console.log("Test 1: Tracking first event");
    this.trackEvent("test_event", { test: "data" });

    // Test 2: Same event immediately should be skipped
    console.log("Test 2: Tracking duplicate event (should be skipped)");
    this.trackEvent("test_event", { test: "data" });

    // Test 3: Different payload should be tracked
    console.log("Test 3: Tracking different payload (should be tracked)");
    this.trackEvent("test_event", { test: "different_data" });

    // Test 4: Different event name should be tracked
    console.log("Test 4: Tracking different event name (should be tracked)");
    this.trackEvent("different_event", { test: "data" });

    console.log("[UserTracking] Deduplication test completed. Check console for results.");
    console.log("Debug info:", this.getDeduplicationDebugInfo());
  }
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use UserTracking.trackEvent() instead
 */
export function track_event(event: string, props: CustomEventProperties = {}) {
  UserTracking.trackEvent(event, props);
}

export { UserTracking };
